<template>
  <b-container fluid>
    <page-loader :loading="!isAuthenticated" />
    <b-row>
      <b-col class="col-forms">
        <b-row class="mb-5">
          <b-col cols="12 text-center mb-3">
            <img src="@assets/images/SS-Logo-Black.png" alt="Sellers Shield Logo" class="img-logo" />
          </b-col>
          <b-col cols="12 text-center">
            <p class="seller-name text-primary-gray-900">
              {{ primarySellerName }}
            </p>
            <p class="text-primary-gray-800 property-address">
              <strong>
                {{ propertyAddress }} <br />
                {{ propertyStateCityZip }}
              </strong>
            </p>
          </b-col>
        </b-row>

        <b-row>
          <b-col cols="12" md="6" offset-md="3">
            <div>
              <h2 class="text-left text-primary-gray-900">Select Disclosures</h2>
              <p class="text-left text-primary-gray-800 mb-0">
                Choose forms, and Sellers Shield™ gets them completed interactively—for free!
              </p>
            </div>

            <div class="form-options">
              <label
                v-for="(form, index) in formOptions"
                :key="index"
                :class="['form-options--option', { selected: selectedForms.includes(form.uuid) }]"
              >
                <div class="d-flex align-items-center justify-content-between">
                  <input
                    type="checkbox"
                    :value="form.uuid"
                    @change="toggleFormSelection(form)"
                    :checked="selectedForms.includes(form.uuid)"
                  />
                  <span class="form-options--option--name">{{ form.name }}</span>
                  <b-img 
                    :src="require('@assets/images/v3/minus-sign.svg')" 
                    v-if="selectedForms.includes(form.uuid)"
                  />
                  <b-img
                    v-else
                    :src="require('@assets/images/v3/plus-sign.svg')" 
                  />
                </div>
              </label>
            </div>

            <div>

              <b-alert :show="isInviteAlreadySent" variant="danger">
                <p>
                  The disclosure forms for this transaction have already been sent.
                </p>
                <p>                  
                  Please reach out to <a href="mailto:protect@sellersshield.com">protect@sellersshield.com</a> for
                  questions regarding the forms for this property.
                </p>
              </b-alert>

              <b-button
                pill
                block
                variant="primary"
                size="lg"
                class="btn-send-forms"
                :disabled="selectedForms.length === 0 || isInviteAlreadySent"
                @click="submitForms"
              >
                Send Forms
              </b-button>
              <div class="d-flex align-items-start align-items-md-center mt-3">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M8.25 5.25H9.75V6.75H8.25V5.25ZM8.25 8.25H9.75V12.75H8.25V8.25ZM9 1.5C4.86 1.5 1.5 4.86 1.5 9C1.5 13.14 4.86 16.5 9 16.5C13.14 16.5 16.5 13.14 16.5 9C16.5 4.86 13.14 1.5 9 1.5ZM9 15C5.6925 15 3 12.3075 3 9C3 5.6925 5.6925 3 9 3C12.3075 3 15 5.6925 15 9C15 12.3075 12.3075 15 9 15Z"
                    fill="#3F5B77"
                  />
                </svg>
                <span class="ml-1 text-sm text-primary-gray-800"
                  >Any required supplemental forms can be sent later using SkySlope Forms.</span
                >
              </div>
            </div>
          </b-col>

          <b-col class="col-brokerage-referral" cols="12" md="6" offset-md="3" v-if="brokerageReferral">
            <BrokerageReferral :company-name="brokerageReferral" />
          </b-col>
        </b-row>
      </b-col>

      <b-col cols="12" class="p-0">
        <div class="disclosure-list">
          <DisclosureList :items="disclosureListItems" />
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import "./skySlopeInvitation.scss";
import { paramsFromQueryString } from "@helpers/router-helper";
import { useAuthStore } from "@store";
import { mapActions } from "pinia";
import { runAsPublic } from "@helpers/api-helper";
import { AppResourceSearch, ApiFilter } from "@sellers-shield-home-protection/ss-core-js";
import InvitationAppResource from "@sellers-shield-home-protection/ss-core-js/src/AppResources/InvitationAppResource";
import UserAppResource from "@sellers-shield-home-protection/ss-core-js/src/AppResources/UserAppResource";
import { setupAxiosInterceptors } from "@helpers/api-helper";
import { isNumber } from "@helpers/string";
import PageLoader from "@components/loaders/PageLoader/PageLoader.vue";
import { fetchEnabledStates } from "@helpers/state";
import { uniqBy } from "lodash";
import { INVITATION_IS_SELLERS_CHOICE } from "@helpers/constants";
import { thirdPartyService } from "@packages/integrations/service";
import DisclosureList from "@packages/guest/components/DisclosureList.vue";
import BrokerageReferral from "@packages/guest/components/BrokerageReferral.vue";

export default {
  components: { BrokerageReferral, PageLoader, DisclosureList },
  name: "SkySlopeInvitation",
  data: () => ({
    accessToken: null,
    isMobile: false,
    forms: [],
    selectedForms: [],
    loading: false,
    params: null,
    property: {
      state: null,
      address: null,
      city: null,
      zip: null,
    },
    primary: {
      firstName: null,
      lastName: null,
      email: null,
      phone: null,
    },
    secondary: {
      firstName: null,
      lastName: null,
      email: null,
      phone: null,
    },
    agent: {
      email: null,
      phone: null,
      licenseId: null,
      licenseState: null,
    },
    thirdParty: {
      developerListingId: null,
      referringPlatform: null,
    },
    agentAppResource: new UserAppResource(),
    invitationAppResource: new InvitationAppResource(),
    thirdPartyIntegrationsAppResource: null,
    states: [],
    disclosureListItems: [
      "Reduced risk for you and your client.",
      "Guided forms with expert tips and resources.",
      "100% complete forms are automatically delivered to your SkySlope Listings.",
      "Optional Home Sale Legal Protection™ with up to $75,000 in paid legal defense for post-sale disputes.",
    ],
  }),
  mounted() {
    let params = paramsFromQueryString(this.$route.query);

    this.params = params;
    this.setInfo(this.params);
    this.checkScreenSize(); // Initial check
    window.addEventListener("resize", this.checkScreenSize); // Listen for window resize
    window.onmessage = async (event) => {
      let data = event.data;
      await this.processMessage(data);
    };
    this.sendMessage("loaded", "awaiting access code");
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.checkScreenSize); // Clean up
  },
  computed: {
    broker(){
      return this.agentAppResource?.broker;
    },
    propertyAddress() {
      return this.property?.address ?? "";
    },
    propertyStateCityZip() {
      return (
        (this.property?.city ?? "") +
          "\n " +
          (this.property?.state?.slug?.toUpperCase() ?? "") +
          ", " +
          (this.property?.zip ?? "") ?? ""
      );
    },
    primarySellerName() {
      return `${this.primary?.firstName ?? ""} ${this.primary?.lastName ?? ""}`;
    },
    isAuthenticated() {
      return this.accessToken;
    },
    formOptions() {
      return uniqBy(this.forms, "uuid");
    },
    brokerageReferral() {
      return this.agentAppResource?.broker?.companyName ?? null;
    },
    isInviteAlreadySent() {
      return !!this.thirdPartyIntegrationsAppResource;
    }
  },
  methods: {
    ...mapActions(useAuthStore, {
      setTokensGetMe: "setTokensGetMe",
    }),
    async processMessage(data) {
      if (data.action) {
        switch (data.action) {
          case "access_code":
            console.log("Show me the data", data);
            await this.getAccessToken(data);

            if (!!this.params) {
              await this.setInfo(this.params);
            }

            break;
          case "pre_fill":
            await this.setInfo(data);
            break;
        }
      }
    },
    checkScreenSize() {
      this.isMobile = window.innerWidth <= 700; // Update based on screen width
    },
    toggleFormSelection(form) {
      if (this.selectedForms.includes(form.uuid)) {
        // Remove form if already selected
        this.selectedForms = this.selectedForms.filter((item) => item !== form.uuid);
      } else {
        // Add form to selected forms
        this.selectedForms.push(form.uuid);
      }
    },
    async submitForms() {
      try {
        let selectedFormsMinusSellerChoice = this.selectedForms.filter((f) => f !== INVITATION_IS_SELLERS_CHOICE);
        let meta = {
          message: this.message ?? "",
          property: {
            city: this.property.city,
            state: this.property.state?.id || this.agentAppResource?.stateId,
            zip: this.property.zip,
            address: this.property.address,
            formattedStreetAddress: this.property.formattedStreetAddress,
          },
          primarySeller: {
            firstName: this.primary.firstName,
            lastName: this.primary.lastName,
            email: this.primary.email.toLowerCase(),
            phone: this.primary.phone,
          },
          secondarySeller: {
            firstName: this.secondary.firstName,
            lastName: this.secondary.lastName,
            email: this.secondary.email || null,
            phone: this.secondary.phone || null,
          },
          agent: {
            id: this.agentAppResource.uuid,
            firstName: this.agentAppResource.firstName,
            lastName: this.agentAppResource.lastName,
          },
          broker: {
            uuid: this.agentAppResource.attributes?.brokerUuid,
          },
          forms: selectedFormsMinusSellerChoice,
          transactionCoordinator: this.agentAppResource?.transactionCoordinatorUuid ?? null,
          // referringPlatformListingId: this.referringPlatformListingId,
          // integrationsContextId: this.integrations.contextId,
          // referringPlatformReturnUrl: this.returnUrl,
          // referringPlatformUuid: this.thirdPartyUuid,
          [INVITATION_IS_SELLERS_CHOICE]: (this.selectedForms.includes(INVITATION_IS_SELLERS_CHOICE)),
        };
        console.log(meta);
        this.invitationAppResource.setAttribute("category", "primary_seller");
        this.invitationAppResource.setAttribute("meta", meta);
        await this.invitationAppResource.save();
        await this.invitationAppResource.find(["order", "order.property", "order.property.state"]);
        let newRecord = await thirdPartyService.createRecord(
          this.thirdParty.developerListingId,
          this.propertyAddress || "new record",
          "skyslope",
          this.invitationAppResource?.orderUuid,
          "order_detail"
        );
        console.log(newRecord);
        // let newRecord = await thirdPartyService.createRecordFromOrderResource(this.thirdParty.developerListingId, this.invitationAppResource.order, this.states, this.thirdParty.referringPlatform, 'order_detail');
        // if (!isEmpty(additionalMeta)) {
        //     Object.assign(meta, additionalMeta);
        // }
        // if (!isEmpty(this.freePack)) {
        //     Object.assign(meta, this.freePack);
        // }
        this.sendMessage("invitation_submitted", {
          invitation: "invitation sent out",
          order: "order has been created and linked to DeveloperListingId: " + this.thirdParty.developerListingId,
        });
      } catch (e) {
        console.error(e.message);
        this.sendMessage("error_submitting_invitation", { message: e });
      }
    },
    async getAccessToken(data) {
      if (data.accessToken) {
        this.accessToken = data.accessToken;
        this.setTokensGetMe(data.accessToken, "", false);
        setupAxiosInterceptors(data.accessToken);
        this.sendMessage("authenticated", "authenticated");
      }
    },
    async fetchResources() {
      this.forms = [];
      await this.fetchStates();
      await this.fetchAgent();
      await this.fetchForms();
    },
    async fetchForms() {
      this.forms = [];
      if (!this.property.state) {
        return false;
      }
      // runAsPublic( async ()=>{
      let searchData = {
        filter: new ApiFilter({
          params: {
            stateId: this.property?.state.id,
            brokerUuid: this.broker ?
              {
                  operator: 'inOrNull',
                  value: [this.broker.uuid]
              }
              : {
                  operator: 'isNull',
                  value: 'isNull',
              },
          },
          relations: {
            forms: new ApiFilter({
              params: {
                isActive: true,
              },
            }),
          },
        }),
        include: ["forms", "forms.parentForm", "forms.sections"],
      };

      let formsetAppResourceSearch = new AppResourceSearch("formset");

      await formsetAppResourceSearch.setSearchData(searchData);
      await formsetAppResourceSearch.search();

      formsetAppResourceSearch.appResourceCollection.toArray().forEach((f) => {
        f.forms.toArray().forEach((form) => {
          this.forms.push(form);
        });
      });
      // });
    },
    async fetchStates() {
      let enabledStates = await fetchEnabledStates();
      enabledStates = enabledStates.toArray().map((state) => ({ value: state.id, text: state.title }));
      this.states = enabledStates;
      this.states = uniqBy(this.states, "value");
      this.states = this.states.sort((a, b) => a.text.localeCompare(b.text));
    },
    async getLuState(stateValue) {
      let state = null;
      await runAsPublic(async () => {
        const searchKey = isNumber(stateValue) ? "id" : [...stateValue].length < 4 ? "slug" : "title";
        const appResourceSearch = new AppResourceSearch("lu-state");

        await appResourceSearch.setSearchData(
          {
            pageSize: 1,
            filter: new ApiFilter({
              params: {
                [searchKey]: stateValue,
              },
            }),
          },
          false
        );

        await appResourceSearch.search();
        [state] = appResourceSearch.appResourceCollection.toArray();
      });

      return state;
    },
    async fetchAgent() {
      if (!this.agent.email) {
        return;
      }
      let email = this.agent.email;
      let user = new AppResourceSearch("user");
      const sd = {
        pageSize: 100,
        include: ["broker", 'licenses', 'nrds'],
        filter: new ApiFilter({
          params: {
            email: { email },
          },
        }),
      };

      await user.setSearchData(sd);
      await user.search();

      if (user.appResourceCollection.toArray().length > 0) {
        this.agentAppResource = user.appResourceCollection.toArray()[0];
      }
    },
    async sendMessage(type, message) {
      window.top.postMessage(
        {
          action: type,
          message: message,
        },
        "*"
      );
    },
    async setInfo(data) {
      const propertiesToSet = [
        { key: "city", source: "propertyCity" },
        { key: "state", source: "propertyState", isAsync: true },
        { key: "address", source: "propertyAddress" },
        { key: "zip", source: "propertyZip" },
        { key: "email", source: "primarySellerEmailAddress", target: "primary" },
        { key: "firstName", source: "primarySellerFirstName", target: "primary" },
        { key: "lastName", source: "primarySellerLastName", target: "primary" },
        { key: "phone", source: "primarySellerPhone", target: "primary" },
        { key: "email", source: "secondarySellerEmailAddress", target: "secondary" },
        { key: "firstName", source: "secondarySellerFirstName", target: "secondary" },
        { key: "lastName", source: "secondarySellerLastName", target: "secondary" },
        { key: "phone", source: "secondarySellerPhone", target: "secondary" },
        { key: "email", source: "agentEmail", target: "agent" },
        { key: "licenseId", source: "agentLicenseId", target: "agent" },
        { key: "licenseState", source: "agentLicenseState", target: "agent" },
        { key: "firstName", source: "agentFirstName", target: "agent" },
        { key: "lastName", source: "agentLastName", target: "agent" },
        { key: "developerListingId", source: "developerListingId", target: "thirdParty" },
        { key: "referringPlatform", source: "referringPlatform", target: "thirdParty" },
      ];

      for (const { key, source, target = "", isAsync } of propertiesToSet) {
        if (data[source]) {
          if (isAsync && key === "state") {
            this.property.state = await this.getLuState(data[source]);
          } else {
            const targetObj = target ? this[target] : this.property;
            targetObj[key] = data[source];
          }
        }
      }

      if (!this.isAuthenticated) {
        return;
      }

      // if Developer Listing Id is provided, lets check if we have already sent forms for this property
      if (this.thirdParty.developerListingId) {
        // Now check if the Developer Listing ID is already in the ThirdPartyIntegrations Table
        const thirdPartyIntegrationsTable = new AppResourceSearch("third-party-integration");
        await thirdPartyIntegrationsTable.setSearchData({
          pageSize: 1,
          andOr: 'and',
          filter: new ApiFilter({
            andOr: 'and',
            params: {
              externalId: this.thirdParty.developerListingId,
            }
          }),
          include: ['pivots']
        }, false);

        await thirdPartyIntegrationsTable.search();

        // If we have a record, it means that we have already sent forms for this property
        if (thirdPartyIntegrationsTable.appResourceCollection.toArray().length > 0) {
          this.thirdPartyIntegrationsAppResource = thirdPartyIntegrationsTable.appResourceCollection.toArray()[0];
        } else {
          this.thirdPartyIntegrationsAppResource = null;
        }
      }

      await this.fetchResources();
    },
  },
};
</script>
